import React, { useState } from "react";
import { MdOutlineCalendarMonth, MdOutlineCalendarToday } from "react-icons/md";
import DashboardCard from "../dashbordCard.tsx";
import ComplaintsChart from "../complaintChart.tsx";
import { ChartOptions } from "chart.js";
import LineChartForCategoryDashbord from "../lineChartForCategory.tsx";
import ComplaintReportTableByCategory from "./ComplaintReportTableByCategory.tsx";
import MadagascarMap from "../madagascarMap.tsx";
import { useAppDispatch, useAppSelector } from "@core/store/hook.ts";
import { dashboardInternData } from "@core/slice/DashboardInternSlice.ts";
import {
  getInternCard,
  getInternStat,
} from "@core/actions/dashboardInternAction.ts";
import { userConnected } from "@core/slice/UsersSlice.ts";
import {
  AgeGroupMapping,
  DatasetStyle,
  MonthMapping,
} from "../dashbord.model.ts";
import ComplaintCategoryReport from "../complaintCategoryReport.tsx";
import { FetchStatusType } from "@core/modeles/model.ts";
import Spinner from "@shared/Spinner.tsx";
import { Button } from "primereact/button";

const MainCategoryDashbord: React.FC = () => {
  const dispatch = useAppDispatch();
  const dataDashboard = useAppSelector(dashboardInternData);
  const internCard = useAppSelector((state) => state.dashboard.internCard);
  const detailsUserConnected = useAppSelector(userConnected);
  const datasetStyle = DatasetStyle;
  const monthMapping = MonthMapping;
  let dataDashbord: any[] = [];
  let dataReportBarChart: any = {};
  const [showMap, setShowMap] = useState(false);

  React.useEffect(() => {
    dispatch(getInternStat());
    dispatch(getInternCard());
  }, [dispatch]);

  const ageGroupMapping = AgeGroupMapping;
  const otherProfil = detailsUserConnected?.profil.accesses[0].description ?? "";
  const profilAdminPsrc = detailsUserConnected?.profil.type === "ADMIN_PSRC";
  const isAdminPsrc = profilAdminPsrc ? "PSRC" : "";

  const profil = profilAdminPsrc ? isAdminPsrc : otherProfil;

  let lineChartTitle = "";
  let lineTableTitle = "";
  let labelsLineChart: string[] = [];
  let datasetLineChart: any[] = [];

  // Conversion de l'age brut en groupe d'âge
  const formatAgeGroupLabel = (rawAgeGroup?: string): string => {
    const found = ageGroupMapping.find((item) => item.key === rawAgeGroup);
    return found ? found.label : rawAgeGroup ?? "";
  };
  
  labelsLineChart = (
    dataDashboard.dataDashboard?.complaintSeriesByGroup.labels || []
  ).map((label) => formatAgeGroupLabel(label));
  datasetLineChart = (
    dataDashboard.dataDashboard?.complaintSeriesByGroup.datasets || []
  ).map((dataset, index) => ({
    label: dataset.label,
    data: [null, ...dataset.data, null],
    ...datasetStyle[index],
  }));

  switch (profil) {
    case "PE":
      lineChartTitle = "Suivi des plaintes liées à la maltraitance des enfants";
      lineTableTitle =
        "Rapport de plaintes liées à la maltraitance des enfants";
      labelsLineChart = labelsLineChart.slice(0, 3);
      break;
    case "VBG":
      lineChartTitle =
        "Suivi des plaintes liées aux violences contre les femmes";
      lineTableTitle =
        "Rapport de plaintes liées aux violences contre les femmes";
      break;
    case "PH/PA":
      lineChartTitle = "Suivi des plaintes relatives à l'inclusion sociale";
      lineTableTitle = "Suivi des plaintes";
      break;
    case "MOCIP":
      lineChartTitle = "Suivi des plaintes relatives à l'inclusion sociale";
      lineTableTitle = "Raport de plaintes relatives à l'inclusion sociale";
      break;
    case "PSRC" :
      lineChartTitle = "Suivi des demandes d'aide sociale lors des crises";
      lineTableTitle = "Rapport de plaintes";
      console.log("Données pour PSRC:", profil);
      const statisticPerMonth =
        dataDashboard.dataDashboard?.statisticPerMonth ?? {};
      const statisticArray = Object.entries(statisticPerMonth).map(
        ([key, value]) => ({
          key,
          value,
        })
      );
      const dataLabels = statisticArray.map((item, index) => {
        if (item.key === monthMapping[index].key) {
          return monthMapping[index].label;
        }
        return [];
      });
      const dataValues = statisticArray.map((item) => item.value);

      dataReportBarChart = {
        labels: dataLabels,
        datasets: [
          {
            label: "Nombre de plaintes",
            data: dataValues,
            backgroundColor: "rgba(54, 162, 235, 0.8)",
            categoryPercentage: 0.3,
            barPercentage: 0.5,
            borderRadius: 10,
          },
        ],
      };
      break;
  }

  if (profil !== "PSRC") {
    dataDashbord = [
      {
        icon: <MdOutlineCalendarMonth />,
        label: "Rapport de plainte annuel",
        value: dataDashboard.dataDashboard?.statisticPerPeriod.year,
      },
      {
        icon: <MdOutlineCalendarMonth />,
        label: "Rapport de plainte mensuel",
        value: dataDashboard.dataDashboard?.statisticPerPeriod.month,
      },
      {
        icon: <MdOutlineCalendarToday />,
        label: "Rapport de plainte hebdomadaire",
        value: dataDashboard.dataDashboard?.statisticPerPeriod.week,
      },
    ];
  } else {
    dataDashbord = [
      {
        icon: <MdOutlineCalendarMonth />,
        label: "Nombre plaintes totales",
        value: dataDashboard.dataDashboard?.count,
      },
      {
        icon: <MdOutlineCalendarMonth />,
        label: "Demande d'aide",
        value: dataDashboard.dataDashboard?.doleanceCount,
      },
      {
        icon: <MdOutlineCalendarToday />,
        label: "Plaintes",
        value: dataDashboard.dataDashboard?.complaintCount,
      },
      {
        icon: <MdOutlineCalendarToday />,
        label: "Taux de satisfaction",
        value: dataDashboard.dataDashboard?.tauxSatisfaction,
      },
    ];
  }

  const dataLineChart = {
    labels: ["", ...labelsLineChart, ""],
    datasets: datasetLineChart,
  };

  // Options du graphique
  const optionsLineChart: ChartOptions<"line"> = {
    responsive: true,
    plugins: {
      legend: {
        position: "bottom",
        display: true,
        onClick: () => {},
        labels: {
          usePointStyle: true,
          boxWidth: 10,
        },
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        title: {
          display: false,
        },

        grid: {
          display: false,
        },
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          callback: function (value) {
            const label = this.getLabelForValue(value as number);
            const maxLength = 8;
            if (!label) return "";
            const words = label.split(" ");
            const lines = [];
            let currentLine = "";
            for (const word of words) {
              if (
                (currentLine + (currentLine ? " " : "") + word).length >
                maxLength
              ) {
                if (currentLine) lines.push(currentLine);
                currentLine = word;
              } else {
                currentLine += (currentLine ? " " : "") + word;
              }
            }
            if (currentLine) lines.push(currentLine);
            return lines;
          },
        },
      },
      y: {
        beginAtZero: true,
        suggestedMax: undefined,
        ticks: {
          stepSize: 1,
          callback: function (value, index, ticks) {
            // Ne pas afficher le dernier tick (valeur max)
            if (index === ticks.length - 1) {
              return "";
            }
            return value;
          },
        },
        afterDataLimits: (scale) => {
          // Étend dynamiquement la limite max de l'axe Y
          scale.max = Math.ceil(scale.max) + 1;
        },
      },
    },
  };

  const optionsReportBarChart: ChartOptions<"bar"> = {
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        type: "category",
        grid: {
          display: false,
        },
      },
      y: {
        type: "linear",
        beginAtZero: true,
        ticks: {
          callback: function (value: any) {
            return (value as number) * 1; // Ajuster la valeur ici si nécessaire
          },
        },
      },
    },
  };
  const handleShowMap = () => {
    setShowMap(true);
  };

  const cardCount = dataDashbord.length
  const smCols = Math.min(cardCount, 2)
  const desktopCols = Math.min(cardCount, 3)
  const lgCols = Math.min(cardCount, 4)

  const classMap : Record<number, string> = {
    1: "grid-cols-1",
    2: "grid-cols-1",
    3: "grid-cols-3",
    4: "grid-cols-4"
  }
  const responsiveClasses = `grid-cols-1 sm:${classMap[smCols]} md:${classMap[desktopCols]} lg:${classMap[lgCols]}`

  return (
    <div className="w-full column gap-4">
      {dataDashboard.loading === FetchStatusType.COMPLETED && !showMap && (
        <>
          <div
            className={`grid gap-4 ${responsiveClasses}`}
          >
            {dataDashbord.map((item, index) => (
              <DashboardCard
                key={index}
                icon={item.icon}
                label={item.label}
                value={item.value}
              />
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-4">
            {(profil === "PSRC") && (
              <ComplaintCategoryReport
                title={"Suivi des plaintes lors de crises"}
                data={dataReportBarChart}
                options={optionsReportBarChart}
              />
            )}
            <LineChartForCategoryDashbord
              title={lineChartTitle}
              data={dataLineChart}
              options={optionsLineChart}
            />
            {(profil !== "PSRC") && (
              <ComplaintsChart
                title="Statut des plaintes"
                databyParent={dataDashboard.dataDashboard?.statisticPerStatus}
                count={dataDashboard.dataDashboard?.count}
              />
            )}
          </div>
          <div className="grid grid-cols-1">
            <ComplaintReportTableByCategory
              onShowMap={handleShowMap}
              title={lineTableTitle}
              dataTableList={dataDashboard.dataDashboard?.complaintCountList}
              typeProfil={profil}
            />
          </div>
        </>
      )}
      {dataDashboard.loading === FetchStatusType.LOADING && (
        <div className="flex justify-center items-center h-screen">
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
          {/* <i className="pi pi-spin pi-spinner text-4xl" style={{ color: "var(--primary-color)" }}></i>   */}
        </div>
      )}
      {/* {(!showMap ) &&} */}
      {showMap && (
        <>
          <div>
            <div className="flex justify-end items-center p-4 rounded-lg mb-4">
              <Button
                label="Retour"
                className="btn btn-secondary w-[120px]"
                onClick={() => setShowMap(false)}
              />
            </div>
          </div>
          <MadagascarMap dataMap={internCard} />
        </>
      )}
    </div>
  );
};

export default MainCategoryDashbord;
